import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Table, Form, Input, Select, DatePicker, Tag, Space,
  Typography, Drawer, Descriptions, Modal, Radio, Upload, Popover, Divider, Statistic, Image, message
} from 'antd';
import { ArrowLeftOutlined, SearchOutlined, ReloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const WITHDRAW_STATUS = {
  pending: { text: '审核中', color: 'processing' },
  approved: { text: '待打款', color: 'warning' },
  rejected: { text: '审核未通过', color: 'error' },
  paid: { text: '已打款', color: 'success' },
};

const MOCK_BANK = {
  accountName: '东莞康林物联网有限公司', bankName: '中国建设银行股份有限公司东莞长安支行',
  bankCode: '105602001052', accountNo: '4420 1562 3000 5271 8888',
};

const PLATFORM_INVOICE_INFO = {
  title: '南京鹤梦信息技术有限公司', taxNo: '91320115MA1TBX9M2Y',
  bank: '中国建设银行股份有限公司南京江宁经济开发区支行', bankAccount: '6221883030028121841',
  address: '南京市江宁区菲尼克斯路70号总部基地32栋1001室（江宁开发区）', phone: '025-84186339',
};

const MOCK_BILLS = [
  { key: '1', billNo: 'BILL202603-002', period: '2026-02', amount: 10676.92 },
  { key: '2', billNo: 'BILL202603-004', period: '2026-02', amount: 5359.50 },
  { key: '3', billNo: 'BILL202604-001', period: '2026-03', amount: 13464.00 },
];

const MOCK_RECORDS = [
  { key: '1', withdrawNo: 'WD20260410001', bills: [{ billNo: 'BILL202604-001', period: '2026-03', amount: 13464.00 }], amount: 13464.00, status: 'pending', applyTime: '2026-04-10 14:00:00', settleTime: null, invoiceType: 'special_e' },
  { key: '2', withdrawNo: 'WD20260405002', bills: [{ billNo: 'BILL202603-002', period: '2026-02', amount: 10676.92 }, { billNo: 'BILL202603-004', period: '2026-02', amount: 5359.50 }], amount: 16036.42, status: 'paid', applyTime: '2026-04-05 10:00:00', settleTime: '2026-04-08 16:30:00', invoiceType: 'special_p', settleVoucher: 'https://placehold.co/600x400?text=结算凭证' },
  { key: '3', withdrawNo: 'WD20260401003', bills: [{ billNo: 'BILL202603-002', period: '2026-02', amount: 10676.92 }], amount: 10676.92, status: 'rejected', rejectReason: '发票信息有误，请重新提交', applyTime: '2026-04-01 09:00:00', settleTime: null, invoiceType: 'special_e' },
  { key: '4', withdrawNo: 'WD20260320004', bills: [{ billNo: 'BILL202603-004', period: '2026-02', amount: 5359.50 }], amount: 5359.50, status: 'approved', applyTime: '2026-03-20 11:00:00', settleTime: null, invoiceType: 'special_p' },
];

const IncomeWithdraw = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [applyVisible, setApplyVisible] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  const [invoiceType, setInvoiceType] = useState('special_e');
  const [billModalVisible, setBillModalVisible] = useState(false);
  const [billModalData, setBillModalData] = useState([]);
  const [bankModalVisible, setBankModalVisible] = useState(false);
  const [voucherVisible, setVoucherVisible] = useState(false);
  const [voucherUrl, setVoucherUrl] = useState('');
  const [invoicePreviewVisible, setInvoicePreviewVisible] = useState(false);

  const selectedAmount = MOCK_BILLS.filter(b => selectedBills.includes(b.key)).reduce((s, b) => s + b.amount, 0);

  const billColumns = [
    { title: '账单编号', dataIndex: 'billNo', width: 180 },
    { title: '账单周期', dataIndex: 'period', width: 120 },
    { title: '结算金额(CNY)', dataIndex: 'amount', width: 140, align: 'right', render: (v) => <Text strong>¥ {v.toFixed(2)}</Text> },
  ];

  const billModalColumns = [
    { title: '账单号', dataIndex: 'billNo', width: 180 },
    { title: '对应账期', dataIndex: 'period', width: 120 },
    { title: '账单金额(CNY)', dataIndex: 'amount', width: 140, align: 'right', render: (v) => <Text strong>¥ {v.toFixed(2)}</Text> },
  ];

  const columns = [
    { title: '提现编号', dataIndex: 'withdrawNo', width: 180 },
    {
      title: '对应账单', key: 'bills', width: 100,
      render: (_, r) => <Button type="link" size="small" onClick={() => { setBillModalData(r.bills); setBillModalVisible(true); }}>{r.bills.length}笔</Button>,
    },
    { title: '提现金额(CNY)', dataIndex: 'amount', width: 140, align: 'right', render: (v) => <Text strong>¥ {v.toFixed(2)}</Text> },
    {
      title: '状态', dataIndex: 'status', width: 120,
      render: (v, r) => {
        const s = WITHDRAW_STATUS[v];
        if (v === 'rejected') return <Popover content={r.rejectReason} title="驳回原因"><Tag color={s.color} style={{ cursor: 'pointer' }}>{s.text}</Tag></Popover>;
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '收款账户', key: 'bank', width: 100,
      render: () => <Button type="link" size="small" onClick={() => setBankModalVisible(true)}>查看</Button>,
    },
    { title: '申请时间', dataIndex: 'applyTime', width: 170 },
    { title: '结算时间', dataIndex: 'settleTime', width: 170, render: (v) => v || null },
    {
      title: '结算凭证', key: 'voucher', width: 100,
      render: (_, r) => r.settleVoucher ? <Button type="link" size="small" onClick={() => { setVoucherUrl(r.settleVoucher); setVoucherVisible(true); }}>查看</Button> : null,
    },
    {
      title: '开票附件', key: 'invoice', width: 100,
      render: (_, r) => <Button type="link" size="small" onClick={() => setInvoicePreviewVisible(true)}>查看</Button>,
    },
  ];

  const handleApply = () => {
    if (selectedBills.length === 0) { message.warning('请选择账单'); return; }
    message.success('提现申请已提交');
    setApplyVisible(false);
    setSelectedBills([]);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/cost/income')} />
        <Title level={4} style={{ margin: 0 }}>收益提现</Title>
      </div>

      {/* 查询条件 */}
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0', marginBottom: 16 }}>
          <Form.Item name="withdrawNo" style={{ marginRight: 8 }}>
            <Input placeholder="提现编号" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="status" style={{ marginRight: 8 }}>
            <Select placeholder="状态" allowClear style={{ width: 130 }}>
              {Object.entries(WITHDRAW_STATUS).map(([k, v]) => <Option key={k} value={k}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" style={{ marginRight: 8 }}>
            <RangePicker placeholder={['开始时间', '结束时间']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

      {/* 列表 */}
      <Card bordered={false}>
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setSelectedBills([]); setInvoiceType('special_e'); setApplyVisible(true); }}>申请提现</Button>
        </div>
        <Table columns={columns} dataSource={MOCK_RECORDS} rowKey="key" scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} style={{ whiteSpace: 'nowrap' }} />
      </Card>

      {/* 申请提现抽屉 */}
      <Drawer title="申请提现" width={700} open={applyVisible} onClose={() => setApplyVisible(false)} destroyOnClose
        footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button onClick={() => setApplyVisible(false)}>取消</Button><Button type="primary" onClick={handleApply}>提交申请</Button></div>}
      >
        <Statistic title="可提现总额（人民币）" value={29500.42} prefix="¥" precision={2} valueStyle={{ color: '#fa8c16', fontSize: 28 }} />
        <Divider />

        <Title level={5}>选择账单</Title>
        <Table columns={billColumns} dataSource={MOCK_BILLS} rowKey="key" pagination={false} size="small"
          rowSelection={{ selectedRowKeys: selectedBills, onChange: setSelectedBills }} />
        <div style={{ marginTop: 8 }}><Text type="secondary">已选金额：</Text><Text strong style={{ color: '#fa8c16' }}>¥ {selectedAmount.toFixed(2)}</Text></div>
        <Divider />

        <Title level={5}>向平台开票</Title>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="发票抬头">{PLATFORM_INVOICE_INFO.title}</Descriptions.Item>
          <Descriptions.Item label="纳税人识别号">{PLATFORM_INVOICE_INFO.taxNo}</Descriptions.Item>
          <Descriptions.Item label="开户行">{PLATFORM_INVOICE_INFO.bank}</Descriptions.Item>
          <Descriptions.Item label="银行账号">{PLATFORM_INVOICE_INFO.bankAccount}</Descriptions.Item>
          <Descriptions.Item label="注册地">{PLATFORM_INVOICE_INFO.address}</Descriptions.Item>
          <Descriptions.Item label="联系电话">{PLATFORM_INVOICE_INFO.phone}</Descriptions.Item>
        </Descriptions>

        <Form layout="vertical">
          <Form.Item label="发票类型" required>
            <Radio.Group value={invoiceType} onChange={(e) => setInvoiceType(e.target.value)}>
              <Radio value="special_e">增值税专用发票（电子）</Radio>
              <Radio value="special_p">增值税专用发票（纸质）</Radio>
            </Radio.Group>
          </Form.Item>
          {invoiceType === 'special_e' && (
            <Form.Item label="上传发票" required>
              <Upload multiple beforeUpload={() => false} accept=".jpg,.jpeg,.png,.pdf" listType="picture-card">
                <div><UploadOutlined /><div style={{ marginTop: 8 }}>上传</div></div>
              </Upload>
            </Form.Item>
          )}
          {invoiceType === 'special_p' && (
            <>
              <Form.Item label="邮寄地址">
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="收件人">何嘉欣</Descriptions.Item>
                  <Descriptions.Item label="联系方式">86-18851194891</Descriptions.Item>
                  <Descriptions.Item label="收件地址">南京市江宁区菲尼克斯路70号总部基地32栋1001室</Descriptions.Item>
                </Descriptions>
              </Form.Item>
              <Form.Item label="上传附件" required extra="请上传发票照片和快递单">
                <Upload multiple beforeUpload={() => false} accept=".jpg,.jpeg,.png,.pdf" listType="picture-card">
                  <div><UploadOutlined /><div style={{ marginTop: 8 }}>上传</div></div>
                </Upload>
              </Form.Item>
            </>
          )}
        </Form>
        <Divider />

        <Title level={5}>提现账户</Title>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="账户名">{MOCK_BANK.accountName}</Descriptions.Item>
          <Descriptions.Item label="开户银行">{MOCK_BANK.bankName}</Descriptions.Item>
          <Descriptions.Item label="开户行号">{MOCK_BANK.bankCode}</Descriptions.Item>
          <Descriptions.Item label="银行账号">{MOCK_BANK.accountNo}</Descriptions.Item>
        </Descriptions>
      </Drawer>

      {/* 对应账单弹窗 */}
      <Modal title="对应账单" open={billModalVisible} onCancel={() => setBillModalVisible(false)} footer={null} width={550}>
        <Table columns={billModalColumns} dataSource={billModalData} rowKey="billNo" pagination={false} size="small" />
      </Modal>

      {/* 收款账户弹窗 */}
      <Modal title="收款账户" open={bankModalVisible} onCancel={() => setBankModalVisible(false)} footer={null}>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="账户名">{MOCK_BANK.accountName}</Descriptions.Item>
          <Descriptions.Item label="开户银行">{MOCK_BANK.bankName}</Descriptions.Item>
          <Descriptions.Item label="开户行号">{MOCK_BANK.bankCode}</Descriptions.Item>
          <Descriptions.Item label="银行账号">{MOCK_BANK.accountNo}</Descriptions.Item>
        </Descriptions>
      </Modal>

      {/* 结算凭证预览 */}
      <Modal title="结算凭证" open={voucherVisible} onCancel={() => setVoucherVisible(false)} footer={null} centered>
        <div style={{ textAlign: 'center' }}><Image src={voucherUrl} style={{ maxWidth: '100%' }} /></div>
      </Modal>

      {/* 开票附件预览 */}
      <Modal title="开票附件" open={invoicePreviewVisible} onCancel={() => setInvoicePreviewVisible(false)} footer={null} centered>
        <div style={{ textAlign: 'center' }}><Image src="https://placehold.co/600x400?text=发票附件" style={{ maxWidth: '100%' }} /></div>
      </Modal>
    </div>
  );
};

export default IncomeWithdraw;
